import { forwardRef, memo, Ref, useEffect, useRef, useState } from 'react';
import { Center, ConfirmPopup } from '~/commonComponents';
import { Field } from '~/commonComponents/InputJob/Field';
import { useToggle } from '~/hooks';
import PlusIcon from '~/images/icons/PlusIcon';
import MailIcon from '~/images/mail-solid.svg';
import { Colors } from '~/themes/colors';
import { InvitePopup } from '../InvitePopup';
import { Title } from './Title';
import { IInputInviteCandidateProps, IInputInviteCandidateValue } from './Types';
import { Value } from './Value';
import { useProfessionalRemoval } from './hooks';
import { fireInterviewRequestInviteCandidatesAdded } from '~/posthogs/events';

const ConfirmationPopupStyle = {
  Description: { width: '95%', marginBottom: 50 },
  LeftButton: { backgroundColor: Colors.secondary_500 },
};

export const Component = (
  { defaultValue = [], onChange, jobPostingId, value: controlledValue }: IInputInviteCandidateProps,
  ref: Ref<HTMLDivElement>,
) => {
  const [internalValue, setInternalValue] = useState<IInputInviteCandidateValue[]>(defaultValue);
  const [isOpen, controls] = useToggle();

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const wasControlledRef = useRef(isControlled);
  useEffect(() => {
    if (!import.meta.env.PROD && wasControlledRef.current !== isControlled) {
      console.warn(
        `Warning: InputInviteCandidate component is changing from ${wasControlledRef.current ? 'controlled' : 'uncontrolled'} to ${isControlled ? 'controlled' : 'uncontrolled'}. ` +
          'Elements should not switch from controlled to uncontrolled (or vice versa).',
      );
    }
    wasControlledRef.current = isControlled;
  }, [isControlled]);

  const doUpdateValue = (newValue: IInputInviteCandidateValue[]) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const {
    context: ctxRemoveProfessional,
    isShowConfirmation: isShowRemoveProfessionalConfirmation,
    handleConfirmRemoval,
    handleDeclineRemoval,
    handleRemoveProfessional,
  } = useProfessionalRemoval({
    onRemoveProfessional: (index: number) => {
      const newValue = currentValue.filter((_, i) => i !== index);
      doUpdateValue(newValue);
    },
  });

  return (
    <>
      <Field
        ref={ref}
        IconSrc={MailIcon}
        SuffixComponent={() => (
          <Center marginRight={-4}>
            <PlusIcon color={Colors.secondary_500} />
          </Center>
        )}
        TitleComponent={Title}
        ValueComponent={() => (
          <Value
            values={currentValue}
            onInviteMoreClick={controls.setOn}
            onRemoveProfessional={handleRemoveProfessional}
          />
        )}
        hideArrow={currentValue.length > 0}
        onToggle={controls.toggle}
      />

      <InvitePopup
        isOpen={isOpen}
        controls={controls}
        jobPostingId={jobPostingId}
        value={currentValue.map((it) => it.value)}
        onConfirm={(it) => {
          fireInterviewRequestInviteCandidatesAdded({
            postingId: jobPostingId,
            totalCandidatesSelected: it.length,
          });

          doUpdateValue(it.map(({ fullName, id }) => ({ label: fullName, value: id })));
          controls.setOff();
        }}
      />

      {isShowRemoveProfessionalConfirmation && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Remove Professional From Invite"
          description={
            <>
              Are you sure you would like to remove{' '}
              <strong>{ctxRemoveProfessional?.value.label}</strong>
              <br />
              from your request?
            </>
          }
          descriptionStyle={ConfirmationPopupStyle.Description}
          leftButtonText="Go Back"
          leftButtonStyle={ConfirmationPopupStyle.LeftButton}
          closePopup={handleDeclineRemoval}
          leftButtonAction={handleDeclineRemoval}
          rightButtonText="Remove"
          rightButtonAction={handleConfirmRemoval}
        />
      )}
    </>
  );
};

export const InputInviteCandidate = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: IInputInviteCandidateProps & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
